import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Upload, 
  Image as ImageIcon, 
  Save, 
  Loader2,
  Camera,
  Trash2,
  AlertTriangle,
  Crop as CropIcon,
  RotateCcw,
  CheckCircle
} from 'lucide-react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { ProjectService, ProjectWithMembers } from '../lib/projects';
import { supabase } from '../lib/supabase';

interface ProjectSettingsProps {
  project: ProjectWithMembers;
  isOpen: boolean;
  onClose: () => void;
  onProjectUpdated: (updatedProject: ProjectWithMembers) => void;
}

// Helper function to create aspect ratio crop
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

// Helper function to get cropped image blob
function getCroppedImg(image: HTMLImageElement, crop: PixelCrop): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = crop.width;
  canvas.height = crop.height;

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
}

export function ProjectSettings({ project, isOpen, onClose, onProjectUpdated }: ProjectSettingsProps) {
  const [formData, setFormData] = useState({
    name: project.name,
    description: project.description || '',
    coverImage: project.cover_image || null,
    deadline: project.deadline || ''
  });
  
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(project.cover_image || null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  
  // Crop-related state
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [processing, setProcessing] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError(null);

    // Create image URL for cropping
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setCropImageSrc(reader.result?.toString() || '');
      setShowCropModal(true);
    });
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 16 / 9)); // 16:9 aspect ratio for cover images
  }, []);

  const handleCropComplete = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      setError('Please select a crop area');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      // Get cropped image blob
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
      
      // Create file from blob
      const croppedFile = new File([croppedBlob], 'cropped-image.jpg', {
        type: 'image/jpeg',
      });

      setImageFile(croppedFile);

      // Create preview URL
      const previewUrl = URL.createObjectURL(croppedBlob);
      setPreviewImage(previewUrl);

      // Close crop modal
      setShowCropModal(false);
      setCropImageSrc('');
    } catch (err) {
      console.error('Failed to crop image:', err);
      setError('Failed to process cropped image');
    } finally {
      setProcessing(false);
    }
  }, [completedCrop]);

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCropImageSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop() || 'jpg';
    const fileName = `project-covers/${project.id}/cover-${Date.now()}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('project-assets')
      .upload(fileName, file);

    if (error) {
      throw new Error(`Failed to upload image: ${error.message}`);
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('project-assets')
      .getPublicUrl(fileName);

    return urlData.publicUrl;
  };

  const handleRemoveImage = () => {
    setPreviewImage(null);
    setImageFile(null);
    setFormData(prev => ({ ...prev, coverImage: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let coverImageUrl = formData.coverImage;

      // Upload new image if selected
      if (imageFile) {
        setUploading(true);
        coverImageUrl = await uploadImage(imageFile);
        setUploading(false);
      }

      // Update project in database
      const updateData: any = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        deadline: formData.deadline || null,
      };

      // Only update cover_image if it changed
      if (coverImageUrl !== project.cover_image) {
        updateData.cover_image = coverImageUrl;
      }

      const updatedProject = await ProjectService.updateProject(project.id, updateData);
      
      // Update the project data in parent component
      onProjectUpdated(updatedProject);
      
      // Close modal
      onClose();
    } catch (err) {
      console.error('Failed to save project:', err);
      setError(err instanceof Error ? err.message : 'Failed to save project');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    setFormData({
      name: project.name,
      description: project.description || '',
      coverImage: project.cover_image || null,
      deadline: project.deadline || ''
    });
    setPreviewImage(project.cover_image || null);
    setImageFile(null);
    setError(null);
    setShowCropModal(false);
    setCropImageSrc('');
    onClose();
  };

  const getDefaultCoverImage = () => 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=800';

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-xl font-semibold text-gray-900">Project Settings</h3>
            <button
              onClick={handleCancel}
              disabled={saving || uploading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Cover Image Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Project Cover Image
              </label>
              
              <div className="space-y-4">
                {/* Image Preview */}
                <div className="relative">
                  <div className="aspect-video w-full bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300">
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt="Project cover preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = getDefaultCoverImage();
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">No cover image</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Remove Image Button */}
                  {previewImage && (
                    <button
                      onClick={handleRemoveImage}
                      disabled={saving || uploading}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors disabled:opacity-50"
                      title="Remove image"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex items-center space-x-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    disabled={saving || uploading}
                  />
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving || uploading}
                    className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                    <span>{uploading ? 'Uploading...' : 'Change Image'}</span>
                  </button>
                  
                  <span className="text-sm text-gray-600">
                    Supported: JPG, PNG, GIF (max 5MB)
                  </span>
                </div>
              </div>
            </div>

            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter project name"
                disabled={saving || uploading}
                maxLength={100}
              />
              <p className="text-xs text-gray-600 mt-1">
                {formData.name.length}/100 characters
              </p>
            </div>

            {/* Project Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                placeholder="Enter project description (optional)"
                disabled={saving || uploading}
                maxLength={500}
              />
              <p className="text-xs text-gray-600 mt-1">
                {formData.description.length}/500 characters
              </p>
            </div>

            {/* Project Deadline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deadline
              </label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={saving || uploading}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-gray-600 mt-1">
                Set a target completion date for this project (optional)
              </p>
            </div>
          </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={handleCancel}
              disabled={saving || uploading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || uploading || !formData.name.trim()}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* Image Crop Modal */}
        <AnimatePresence>
          {showCropModal && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col"
              >
                {/* Crop Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center space-x-3">
                    <CropIcon className="w-6 h-6 text-purple-600" />
                    <h3 className="text-xl font-semibold text-gray-900">Crop Image</h3>
                  </div>
                  <button
                    onClick={handleCropCancel}
                    disabled={processing}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Crop Interface - Scrollable */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-6 flex flex-col items-center justify-center bg-gray-50 min-h-full">
                  <div className="max-w-full max-h-[60vh] overflow-auto">
                    {cropImageSrc && (
                      <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={16 / 9}
                        minWidth={200}
                        minHeight={112}
                      >
                        <img
                          ref={imgRef}
                          alt="Crop me"
                          src={cropImageSrc}
                          onLoad={onImageLoad}
                          className="max-w-full h-auto"
                        />
                      </ReactCrop>
                    )}
                  </div>
                  
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      Drag to adjust the crop area. The image will be cropped to 16:9 aspect ratio.
                    </p>
                    <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                      <span>• Minimum size: 200x112px</span>
                      <span>• Aspect ratio: 16:9</span>
                      <span>• Format: JPEG</span>
                    </div>
                  </div>
                  </div>
                </div>

                {/* Crop Modal Footer */}
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-white flex-shrink-0">
                  <button
                    onClick={handleCropCancel}
                    disabled={processing}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropComplete}
                    disabled={processing || !completedCrop}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Apply Crop</span>
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
      )}
    </AnimatePresence>
  );
} 